import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useBusinessAI } from "../../../../hooks/useBusinessAI";
import { useSubscription } from "../../../../hooks/useSubscription";

interface ScoreData {
  overall: number;
  categories: {
    marketOpportunity: number;
    customerUnderstanding: number;
    valueAndProduct: number;
    businessModel: number;
    validationExecution: number;
  };
  categoryReasons?: {
    marketOpportunity?: string;
    customerUnderstanding?: string;
    valueAndProduct?: string;
    businessModel?: string;
    validationExecution?: string;
  };
  redFlags: string[];
  strengths?: string[];
  recommendations: string[];
  calculatedAt?: { seconds: number };
  aiEvaluated?: boolean;
}

interface Props {
  className: string;
  projectId: string;
  existingScore?: ScoreData;
}

const categoryLabels: Record<string, { label: string; icon: string; color: string }> = {
  marketOpportunity: {
    label: "Market Opportunity",
    icon: "bi-graph-up-arrow",
    color: "primary",
  },
  customerUnderstanding: {
    label: "Customer Understanding",
    icon: "bi-people",
    color: "primary",
  },
  valueAndProduct: {
    label: "Value & Product",
    icon: "bi-box-seam",
    color: "primary",
  },
  businessModel: {
    label: "Business Model",
    icon: "bi-currency-dollar",
    color: "primary",
  },
  validationExecution: {
    label: "Validation & Execution",
    icon: "bi-check2-circle",
    color: "primary",
  },
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return "success";
  if (score >= 60) return "primary";
  if (score >= 40) return "warning";
  return "danger";
};

const getScoreLabel = (score: number): string => {
  if (score >= 80) return "Excellent";
  if (score >= 60) return "Good";
  if (score >= 40) return "Developing";
  if (score >= 20) return "Early Stage";
  return "Just Started";
};

export const BusinessScoreWidget: React.FC<Props> = ({
  className,
  projectId,
  existingScore,
}) => {
  const [score, setScore] = useState<ScoreData | null>(existingScore || null);
  const [loading, setLoading] = useState(false);
  const { calculateScore, scoring, error } = useBusinessAI();
  const { canUseFeature } = useSubscription();
  const showDetailedScoring = canUseFeature("detailed_scoring");

  const handleRecalculate = async () => {
    setLoading(true);
    const result = await calculateScore(projectId);
    if (result) {
      setScore({
        overall: result.overallScore,
        categories: result.categoryScores,
        categoryReasons: result.categoryReasons,
        redFlags: result.redFlags,
        strengths: result.strengths,
        recommendations: result.recommendations,
        aiEvaluated: true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!existingScore && projectId) {
      handleRecalculate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const isLoading = loading || scoring;

  return (
    <div className={`card ${className}`}>
      {/* Header */}
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bolder text-dark fs-3">
            Business Strength Score
          </span>
          <span className="text-muted mt-2 fw-bold fs-6">
            Based on 24-step validation framework
          </span>
        </h3>
        <div className="card-toolbar">
          <button
            className="btn btn-sm btn-light-primary"
            onClick={handleRecalculate}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Calculating...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-clockwise me-1"></i>
                Recalculate
              </>
            )}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="card-body pt-5">
        {error && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {score ? (
          <div className="row g-4">
            {/* Score Circle - smaller column */}
            <div className="col-xl-2 col-lg-3 col-md-6">
              <div className="d-flex flex-column align-items-center">
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle bg-light-${getScoreColor(
                    score.overall
                  )}`}
                  style={{ width: "80px", height: "80px", minWidth: "80px" }}
                >
                  <div className="text-center">
                    <span className={`fs-2x fw-bolder text-${getScoreColor(score.overall)}`}>
                      {score.overall}
                    </span>
                    <span className="text-muted fs-9 d-block">/100</span>
                  </div>
                </div>
                <span className={`badge badge-light-${getScoreColor(score.overall)} fs-7 mt-3`}>
                  {getScoreLabel(score.overall)}
                </span>
              </div>
            </div>

            {/* Strengths - wider column */}
            <div className="col-xl col-lg-3 col-md-6">
              {!showDetailedScoring ? (
                <div className="text-center py-5">
                  <i className="bi bi-lock text-gray-400 fs-2x d-block mb-3"></i>
                  <p className="text-muted fs-7 mb-2">Detailed breakdown</p>
                  <Link to="/profile/subscription" className="btn btn-sm btn-light-primary">
                    Upgrade to Pro
                  </Link>
                </div>
              ) : score.strengths && score.strengths.length > 0 ? (
                <>
                  <h6 className="fw-bolder mb-3 text-success fs-6">
                    <i className="bi bi-trophy text-success me-2 fs-5"></i>
                    Strengths
                  </h6>
                  {score.strengths.slice(0, 2).map((strength, index) => (
                    <div key={index} className="d-flex align-items-start mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2 mt-1 fs-7"></i>
                      <span className="text-gray-700 fs-7">{strength}</span>
                    </div>
                  ))}
                </>
              ) : null}
            </div>

            {/* Red Flags - wider column */}
            <div className="col-xl col-lg-3 col-md-6">
              {showDetailedScoring && score.redFlags && score.redFlags.length > 0 && (
                <>
                  <h6 className="fw-bolder mb-3 text-danger fs-6">
                    <i className="bi bi-exclamation-triangle text-danger me-2 fs-5"></i>
                    Areas to Address
                  </h6>
                  {score.redFlags.slice(0, 2).map((flag, index) => (
                    <div key={index} className="d-flex align-items-start mb-2">
                      <i className="bi bi-x-circle-fill text-danger me-2 mt-1 fs-7"></i>
                      <span className="text-gray-600 fs-7">{flag}</span>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Recommendations - wider column */}
            <div className="col-xl col-lg-3 col-md-6">
              {showDetailedScoring && score.recommendations && score.recommendations.length > 0 && (
                <>
                  <h6 className="fw-bolder mb-3 text-primary fs-6">
                    <i className="bi bi-lightbulb text-primary me-2 fs-5"></i>
                    Next Steps
                  </h6>
                  {score.recommendations.slice(0, 2).map((rec, index) => (
                    <div key={index} className="d-flex align-items-start mb-2">
                      <span className="badge badge-circle badge-primary me-2 mt-1" style={{ minWidth: "18px", height: "18px", fontSize: "9px" }}>
                        {index + 1}
                      </span>
                      <span className="text-gray-700 fs-7">{rec}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-10">
            {isLoading ? (
              <>
                <div className="spinner-border text-primary mb-4"></div>
                <p className="text-muted">Calculating your business score...</p>
              </>
            ) : (
              <>
                <i className="bi bi-bar-chart-line-fill fs-3tx text-muted mb-4 d-block"></i>
                <p className="text-muted mb-4">
                  Complete more steps to generate your business strength score
                </p>
                <button
                  className="btn btn-primary"
                  onClick={handleRecalculate}
                >
                  <i className="bi bi-calculator me-2"></i>
                  Calculate Score
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
